/**
 * PCA Orientation Analysis Component
 * Core UI for element orientation analysis and visualization
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { PCAOrientationTool } from '@/services/tools/PCAOrientationTool';
import { DEFAULT_PCA_CONFIG, AnalysisStatus } from '@/types/orientation';
import type { 
  OrientationVisualization, 
  OrientationToolState, 
  PCAConfig
} from '@/types/orientation';
import { 
  Compass, 
  MousePointer, 
  Loader2, 
  Eye, 
  Trash2, 
  AlertCircle,
  Settings,
  Check,
  X
} from 'lucide-react';

export function OrientationAnalysis() {
  const { viewer, isModelLoaded } = useViewerStore();
  
  const [state, setState] = useState<OrientationToolState>({
    mode: 'idle',
    results: [],
    selectedResult: undefined,
    isProcessing: false,
    error: undefined,
    config: DEFAULT_PCA_CONFIG
  });

  const [showSettings, setShowSettings] = useState(false);
  const [selectionHandler, setSelectionHandler] = useState<any>(null);
  const [manualDbId, setManualDbId] = useState<string>('');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (viewer && state.results.length > 0) {
        PCAOrientationTool.clearAllArrows(viewer);
      }
      if (selectionHandler) {
        viewer?.removeEventListener(
          window.Autodesk.Viewing.SELECTION_CHANGED_EVENT,
          selectionHandler
        );
      }
    };
  }, []);

  /**
   * Start element selection mode
   */
  const startSelection = useCallback(() => {
    if (!viewer || !isModelLoaded) return;

    setState(prev => ({ ...prev, mode: 'selecting', error: undefined }));

    // Create selection handler
    const handler = async (event: any) => {
      if (event.dbIdArray && event.dbIdArray.length > 0) {
        const dbId = event.dbIdArray[0];
        
        // Remove handler
        viewer.removeEventListener(
          window.Autodesk.Viewing.SELECTION_CHANGED_EVENT,
          handler
        );
        setSelectionHandler(null);

        // Analyze element
        await analyzeElement(dbId);
      }
    };

    // Add selection listener
    viewer.addEventListener(
      window.Autodesk.Viewing.SELECTION_CHANGED_EVENT,
      handler
    );
    setSelectionHandler(handler);

    // Clear current selection to prepare for new selection
    viewer.clearSelection();
  }, [viewer, isModelLoaded]);

  /**
   * Cancel selection mode
   */
  const cancelSelection = useCallback(() => {
    if (selectionHandler && viewer) {
      viewer.removeEventListener(
        window.Autodesk.Viewing.SELECTION_CHANGED_EVENT,
        selectionHandler
      );
      setSelectionHandler(null);
    }
    setState(prev => ({ ...prev, mode: 'idle' }));
  }, [viewer, selectionHandler]);

  /**
   * Analyze selected element
   */
  const analyzeElement = async (dbId: number) => {
    if (!viewer) return;

    setState(prev => ({ ...prev, mode: 'analyzing', isProcessing: true, error: undefined }));

    try {
      const result = await PCAOrientationTool.analyzeAndVisualizeElementOrientation(
        dbId,
        viewer,
        state.config
      );

      if (result) {
        const visualization = PCAOrientationTool.createVisualization(result, viewer);
        
        setState(prev => ({
          ...prev,
          mode: 'results',
          results: [...prev.results, visualization],
          selectedResult: visualization,
          isProcessing: false
        }));
      } else {
        setState(prev => ({
          ...prev,
          mode: 'idle',
          isProcessing: false,
          error: `Element ${dbId} not found or has no geometry. Please verify the ID exists in the model.`
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        mode: 'idle',
        isProcessing: false,
        error: 'Failed to analyze element orientation'
      }));
    }
  };

  /**
   * Handle manual dbId input
   */
  const handleManualAnalysis = () => {
    const dbId = parseInt(manualDbId.trim());
    
    if (isNaN(dbId) || dbId <= 0) {
      setState(prev => ({ 
        ...prev, 
        error: 'Please enter a valid element ID (positive number)' 
      }));
      return;
    }

    // Clear input after successful parse
    setManualDbId('');
    analyzeElement(dbId);
  };

  /**
   * View element with arrow
   */
  const viewOrientation = (visualization: OrientationVisualization) => {
    if (!viewer) return;
    
    PCAOrientationTool.focusOnOrientation(visualization, viewer, state.config);
    setState(prev => ({ ...prev, selectedResult: visualization }));
  };

  /**
   * Clear specific result
   */
  const clearResult = (visualization: OrientationVisualization) => {
    if (!viewer) return;

    PCAOrientationTool.clearArrowsForElement(viewer, visualization.result.dbId);
    
    setState(prev => ({
      ...prev,
      results: prev.results.filter(r => r !== visualization),
      selectedResult: prev.selectedResult === visualization ? undefined : prev.selectedResult
    }));
  };

  /**
   * Clear all results
   */
  const clearAllResults = () => {
    if (!viewer) return;

    PCAOrientationTool.clearAllArrows(viewer);
    viewer.clearThemingColors();
    viewer.showAll();
    
    setState(prev => ({
      ...prev,
      results: [],
      selectedResult: undefined,
      mode: 'idle'
    }));
  };

  /**
   * Update configuration
   */
  const updateConfig = (updates: Partial<PCAConfig>) => {
    setState(prev => ({
      ...prev,
      config: { ...prev.config, ...updates }
    }));
  };

  // Render based on model state
  if (!isModelLoaded) {
    return (
      <div className="text-center py-8 text-gray-400">
        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
        <p>No model loaded</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Buttons and Manual Input */}
      <div className="space-y-2">
        {state.mode === 'idle' && (
          <>
            {/* Selection and Settings Row */}
            <div className="flex gap-2">
              <button
                onClick={startSelection}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                         flex items-center justify-center gap-2 transition-colors"
              >
                <MousePointer className="w-4 h-4" />
                Select Element
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="px-3 py-2 bg-dark-200 hover:bg-dark-300 text-gray-300 rounded-lg"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>

            {/* Manual Input Row */}
            <div className="flex gap-2">
              <input
                type="text"
                value={manualDbId}
                onChange={(e) => setManualDbId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleManualAnalysis();
                  }
                }}
                placeholder="Enter element ID (dbId)"
                className="flex-1 px-3 py-2 bg-dark-200 border border-gray-600 rounded-lg text-white 
                         placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              />
              <button
                onClick={handleManualAnalysis}
                disabled={!manualDbId.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 
                         disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Analyze
              </button>
            </div>

          </>
        )}

        {state.mode === 'selecting' && (
          <div className="flex items-center justify-between px-4 py-2 bg-blue-900/30 
                        border border-blue-600 rounded-lg">
            <span className="text-blue-300 flex items-center gap-2">
              <MousePointer className="w-4 h-4 animate-pulse" />
              Click on an element in the model...
            </span>
            <button
              onClick={cancelSelection}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {state.mode === 'analyzing' && (
          <div className="px-4 py-2 bg-dark-200 rounded-lg flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            <span className="text-gray-300">Analyzing orientation...</span>
          </div>
        )}

        {state.mode === 'results' && state.results.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={startSelection}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                       flex items-center justify-center gap-2 transition-colors"
            >
              <MousePointer className="w-4 h-4" />
              Analyze Another
            </button>
            <button
              onClick={clearAllResults}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-3 bg-dark-200 rounded-lg space-y-3">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Analysis Settings</h4>
          
          <div className="space-y-2">
            <label className="flex items-center justify-between text-xs text-gray-400">
              <span>Max Vertices</span>
              <input
                type="number"
                value={state.config.maxVertices}
                onChange={(e) => updateConfig({ maxVertices: parseInt(e.target.value) })}
                className="w-20 px-2 py-1 bg-dark-100 border border-gray-600 rounded text-white"
              />
            </label>

            <label className="flex items-center justify-between text-xs text-gray-400">
              <span>Arrow Length</span>
              <input
                type="number"
                value={state.config.arrowLength}
                onChange={(e) => updateConfig({ arrowLength: parseFloat(e.target.value) })}
                className="w-20 px-2 py-1 bg-dark-100 border border-gray-600 rounded text-white"
                step="0.5"
              />
            </label>

            <label className="flex items-center justify-between text-xs text-gray-400">
              <span>Min Confidence</span>
              <input
                type="number"
                value={state.config.minConfidence}
                onChange={(e) => updateConfig({ minConfidence: parseFloat(e.target.value) })}
                className="w-20 px-2 py-1 bg-dark-100 border border-gray-600 rounded text-white"
                step="0.1"
                min="0"
                max="1"
              />
            </label>

            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={state.config.enableSampling}
                onChange={(e) => updateConfig({ enableSampling: e.target.checked })}
                className="rounded"
              />
              <span>Enable vertex sampling for large meshes</span>
            </label>
          </div>
        </div>
      )}

      {/* Error Display */}
      {state.error && (
        <div className="p-3 bg-red-900/20 border border-red-600 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
          <p className="text-sm text-red-300">{state.error}</p>
        </div>
      )}

      {/* Results List */}
      {state.results.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Analysis Results</h4>
          
          {state.results.map((viz, index) => (
            <div
              key={viz.arrowId}
              className={`p-3 bg-dark-200 rounded-lg border transition-colors ${
                state.selectedResult === viz 
                  ? 'border-blue-500' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Compass className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-gray-200">
                      {viz.result.elementName || `Element ${viz.result.dbId}`}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>ID: {viz.result.dbId}</span>
                    <span>Confidence: {(viz.result.confidence * 100).toFixed(0)}%</span>
                  </div>

                  <div className="text-xs text-gray-500">
                    Direction: ({viz.result.direction.x.toFixed(2)}, {viz.result.direction.y.toFixed(2)})
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => viewOrientation(viz)}
                    className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    title="View element with arrow"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => clearResult(viz)}
                    className="p-1.5 bg-dark-300 hover:bg-red-600 text-gray-400 hover:text-white 
                             rounded transition-colors"
                    title="Remove this result"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Confidence indicator */}
              <div className="mt-2 h-1 bg-dark-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    viz.result.confidence > 0.7 ? 'bg-green-500' :
                    viz.result.confidence > 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${viz.result.confidence * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      {state.mode === 'idle' && state.results.length === 0 && (
        <div className="text-center py-4 text-gray-500 text-sm">
          <p>Analyze element geometry to find its primary axis</p>
          <p className="text-xs mt-1">Uses Principal Component Analysis (PCA)</p>
        </div>
      )}
    </div>
  );
}